import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchWithCsrf } from "@/lib/api";
import type {
  ApiToken,
  CreateApiTokenData,
  CreateApiTokenResponse,
} from "@/types/auth";
import type { ApiSuccess, ApiError } from "@/types/api";

export const API_TOKENS_QUERY_KEY = ["apiTokens"] as const;

interface ApiTokensResponse {
  apiTokens: ApiToken[];
}

export function useApiTokens() {
  return useQuery<ApiToken[]>({
    queryKey: API_TOKENS_QUERY_KEY,
    queryFn: async () => {
      const result = await fetchWithCsrf<ApiSuccess<ApiTokensResponse>>(
        "/api/api_tokens"
      );
      return result.data.apiTokens;
    },
  });
}

export function useCreateApiToken() {
  const queryClient = useQueryClient();

  return useMutation<
    ApiSuccess<CreateApiTokenResponse>,
    ApiError,
    CreateApiTokenData
  >({
    mutationFn: async (data) => {
      return fetchWithCsrf("/api/api_tokens", {
        method: "POST",
        body: JSON.stringify({ apiToken: data }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: API_TOKENS_QUERY_KEY });
    },
  });
}

export function useRevokeApiToken() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: async (tokenId) => {
      await fetchWithCsrf(`/api/api_tokens/${tokenId}`, { method: "DELETE" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: API_TOKENS_QUERY_KEY });
    },
  });
}
